import { useFormContext, useFormState } from 'react-hook-form';
import { selectUserProject } from 'core/userContext/selectors';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { TopTooltip } from 'ui/tooltip';
import { Modal, ModalBody, ModalFooter, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { CLONE_JOURNEY_MODAL } from '../../../constants';
import { closeModal, getWorkflow } from '../../../ducks';
import { CloneJourney } from './CloneJourney';
import { useCloningCampaignsContext } from './context';
import { cloneJourney } from './utils';
import { CLONE_JOURNEY_BUTTON_TEST_ID } from './constants';
import { useCloneJourneyCrossProject } from './hooks';

const StyledContainer = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  > :not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const StyledText = styled.div`
  display: flex;
  text-align: center;
`;

export type Props = {
  /** Whether modal is open and visible */
  isOpen: boolean;
};

export const CloneJourneyModal = ({ isOpen }: Props) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const currentProject = useSelector(selectUserProject);
  const { getValues } = useFormContext();
  const { isValid } = useFormState();

  const { selectedProject, wfNameInput, copyWebhooks, copyCampaigns, containerRef } =
    useCloningCampaignsContext();

  const isDifferentProject = selectedProject !== currentProject?.id;
  const isCrossProject = selectedProject && isDifferentProject;

  const { handleCloneJourney } = useCloneJourneyCrossProject({
    workflowName: wfNameInput,
  });

  const closeCloneJourneyModal = () => {
    // don't close modal if user is editing text
    if (!window?.getSelection()?.toString()) dispatch(closeModal(CLONE_JOURNEY_MODAL));
  };

  const onCloneJourney = () => {
    const campaignValues = getValues();

    if (isCrossProject)
      handleCloneJourney(
        { workflowType: workflow.workflowType, id: workflow.id, name: wfNameInput },
        selectedProject,
        copyWebhooks,
        copyCampaigns ? campaignValues : {},
      );
    else cloneJourney(workflow, dispatch, wfNameInput);

    dispatch(closeModal(CLONE_JOURNEY_MODAL));
  };

  const hasCloningConfigurationError = !!isCrossProject && copyCampaigns && !isValid;
  const tooltipContent = (
    <StyledText>Journey cannot be copied with the current configuration</StyledText>
  );

  return (
    <Modal
      id={CLONE_JOURNEY_MODAL}
      isModalOpen={isOpen}
      onClose={closeCloneJourneyModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
      withPortal
    >
      <ModalHeader>Copy journey</ModalHeader>
      <ModalBody>
        <StyledContainer ref={containerRef}>
          <CloneJourney />
        </StyledContainer>
      </ModalBody>
      <ModalFooter cancelLabel={'Cancel'}>
        <TopTooltip isEnabled={hasCloningConfigurationError} tooltipContent={tooltipContent}>
          <Button
            autoFocus
            data-test={CLONE_JOURNEY_BUTTON_TEST_ID}
            onClick={onCloneJourney}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
            isDisabled={hasCloningConfigurationError}
          >
            Copy journey
          </Button>
        </TopTooltip>
      </ModalFooter>
    </Modal>
  );
};
